import {
    areSearchParamsEqual,
    TRUNKS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type TrunksFilterItem = { key: string; value: any };

export type TrunksUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: TrunksFilterItem | null;
    filterCampaignName: TrunksFilterItem | null;
    filterPhoneCount: TrunksFilterItem | null;
    filterQueueActive: TrunksFilterItem[] | null;
    uiFilters: Record<string, any>;
};

export type TrunksUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    archiveTableToggle: boolean;
    filterCampaignId: TrunksFilterItem | null;
    filterCampaignName: TrunksFilterItem | null;
    filterPhoneCount: TrunksFilterItem | null;
    filterQueueActive: TrunksFilterItem[] | null;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialTrunksUrlState(): TrunksUrlViewState {
    return parseTrunksUrlState(getHashSearchParams());
}

export function parseTrunksUrlState(params: URLSearchParams): TrunksUrlViewState {
    params = unwrapEncodedSearchParams(params, TRUNKS_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const archiveTableToggle = !parseBooleanParam(params.get('archived'), false);

    const id = params.get('id');
    const name = params.get('name');
    const phonesCount = params.get('phonesCount');
    const isActive = getMultiParams(params, 'isActive').map(toBooleanValue).filter((value) => value !== null);

    if (id) uiFilters.id = id;
    if (name) uiFilters.name = name;
    if (phonesCount) uiFilters.phonesCount = phonesCount;
    if (isActive.length) uiFilters.isActive = isActive;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        archiveTableToggle,
        filterCampaignId: toFilterItem('Id', id),
        filterCampaignName: toFilterItem('Name', name),
        filterPhoneCount: toFilterItem('PhonesCount', phonesCount),
        filterQueueActive: toFilterList('IsActive', isActive),
        uiFilters,
    };
}

export function buildTrunksSearchParams(state: TrunksUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainTrunksSearchParams(state), TRUNKS_ENCODED_QUERY_KEY);
}

function buildPlainTrunksSearchParams(state: TrunksUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }
    if (!state.archiveTableToggle) {
        params.set('archived', '1');
    }

    setScalar(params, 'id', state.filterCampaignId?.value);
    setScalar(params, 'name', state.filterCampaignName?.value);
    setScalar(params, 'phonesCount', state.filterPhoneCount?.value);
    setMulti(params, 'isActive', state.filterQueueActive);

    return params;
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: TrunksFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value === undefined || item?.value === null || item.value === '') return;
        params.append(key, normalizeBooleanParam(item.value));
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): TrunksFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<any>): TrunksFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value }));
}

function toBooleanValue(value: string): boolean | null {
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
    return null;
}

function normalizeBooleanParam(value: any): string {
    if (value === true || value === 'true' || value === '1') return '1';
    if (value === false || value === 'false' || value === '0') return '0';
    return String(value);
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}
